import React from "react";
import { Container, Grid, Button, Typography, Box, Stack } from "@mui/material";
import { Link } from "react-scroll";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CountUp from "./CountUp";
import { colors, fonts } from "../theme";

const highlights = [
  {
    title: "Our Mission",
    text: "Deliver embedded, real-time technical mentorship that meets industry and accreditation requirements without compromise.",
  },
  {
    title: "Our Vision",
    text: "To transform partner campuses into distinguished regional hubs for research, innovation and student placements.",
  },
  {
    title: "Founder's Message",
    text: "“Consistency is growth.” We partner with leadership to turn long-term goals into measurable outcomes. — Rasheedha R",
  },
];

const trustBadges = ["NAAC", "NBA", "NIRF", "IQAC", "ARIIA / IIC"];

const heroStats = [
  { value: "1000+", label: "Students trained" },
  { value: "25+", label: "Partner institutions" },
  { value: "35%", label: "Rise in day-one placements" },
  { value: "100%", label: "Unique student repos" },
];

const Home = () => {
  return (
    <section id="home-section" className="home-section">
      <Box
        sx={{
          position: "relative",
          background: colors.heroGradient,
          color: colors.white,
          pt: { xs: 7, md: 11 },
          pb: { xs: 8, md: 12 },
          overflow: "hidden",
        }}
      >
        {/* Subtle blue glow accents — depth without leaving the palette */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(56,182,255,0.28) 0%, rgba(56,182,255,0) 70%)",
            pointerEvents: "none",
            animation: "blobFloat 11s ease-in-out infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: -140,
            left: -100,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,102,196,0.14) 0%, rgba(255,102,196,0) 70%)",
            pointerEvents: "none",
            animation: "blobFloat 14s ease-in-out infinite reverse",
          }}
        />

        <Container sx={{ position: "relative" }}>
          <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
            {/* Left — message */}
            <Grid item xs={12} md={7}>
              <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2.5 }}>
                <Box sx={{ width: 26, height: 3, borderRadius: 2, bgcolor: colors.pink }} />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: colors.blue,
                    textTransform: "uppercase",
                  }}
                >
                  Strategic Institutional Partnership
                </Typography>
              </Stack>

              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontFamily: fonts.heading,
                  fontWeight: 800,
                  lineHeight: 1.12,
                  fontSize: { xs: "2.1rem", sm: "2.7rem", md: "3.2rem" },
                  mb: 2.5,
                  letterSpacing: "-0.01em",
                }}
              >
                Your Academic Partner for{" "}
                <Box component="span" sx={{ color: colors.blue }}>
                  Research, Innovation &amp; Technology
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.12rem" },
                  maxWidth: 640,
                  mb: 2,
                  color: "rgba(255,255,255,0.86)",
                  lineHeight: 1.7,
                }}
              >
                Crescent EdTech is a deeply embedded Research &amp; Innovation Enablement Partner —
                building accreditation-ready outcomes across NAAC, NBA and NIRF through original
                research, industry-grade internships and continuous faculty development.
              </Typography>

              <Stack
                direction="row"
                spacing={1.2}
                sx={{ mb: 4, color: "rgba(255,255,255,0.62)", fontWeight: 600, letterSpacing: "0.04em", fontSize: "0.9rem", flexWrap: "wrap" }}
              >
                <Box component="span" sx={{ color: colors.white }}>Learn</Box>
                <Box component="span">/</Box>
                <Box component="span" sx={{ color: colors.white }}>Practice</Box>
                <Box component="span">/</Box>
                <Box component="span" sx={{ color: colors.white }}>Implement</Box>
                <Box component="span">/</Box>
                <Box component="span" sx={{ color: colors.white }}>Career</Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Link to="contacts-section" smooth={true} duration={800} offset={-70}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: colors.blue,
                      color: colors.navy,
                      px: 3.5,
                      py: 1.25,
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      "&:hover": { bgcolor: "#5cc3ff" },
                    }}
                  >
                    Request a Discovery Consultation
                  </Button>
                </Link>
                <Link to="programs-section" smooth={true} duration={800} offset={-70}>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      color: colors.white,
                      borderColor: "rgba(255,255,255,0.45)",
                      px: 3.5,
                      py: 1.25,
                      fontFamily: fonts.body,
                      "&:hover": { borderColor: colors.white, bgcolor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Explore Programs
                  </Button>
                </Link>
              </Stack>

              <Box sx={{ mt: 5 }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: "rgba(255,255,255,0.55)",
                    textTransform: "uppercase",
                    mb: 1.6,
                  }}
                >
                  Alignable with national quality frameworks
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.2}>
                  {trustBadges.map((b) => (
                    <Box
                      key={b}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        bgcolor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        borderRadius: "8px",
                        px: 1.5,
                        py: 0.6,
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      <VerifiedIcon sx={{ fontSize: 15, color: colors.blue }} /> {b}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* Right — credential / stat panel */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 4,
                  p: { xs: 3, md: 3.5 },
                  backdropFilter: "blur(6px)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: colors.blue,
                    textTransform: "uppercase",
                    mb: 2.5,
                  }}
                >
                  Measured institutional impact
                </Typography>
                <Grid container spacing={2}>
                  {heroStats.map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <Typography
                        sx={{
                          fontFamily: fonts.heading,
                          fontWeight: 800,
                          fontSize: { xs: "1.9rem", md: "2.15rem" },
                          lineHeight: 1,
                          color: colors.white,
                        }}
                      >
                        <CountUp value={s.value} />
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.82rem", mt: 0.6, lineHeight: 1.4 }}>
                        {s.label}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: "0.88rem", lineHeight: 1.6 }}>
                    Long-term MoU-based engagement — not seasonal vendor workshops.
                    Embedded mentorship your IQAC can document.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission / Vision / Founder band */}
      <Box sx={{ background: colors.navy, color: colors.white, py: { xs: 5, md: 6 } }}>
        <Container>
          <Grid container spacing={4}>
            {highlights.map((h) => (
              <Grid item xs={12} md={4} key={h.title}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontFamily: fonts.heading, fontWeight: 700, mb: 1, color: colors.blue }}
                >
                  {h.title}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.7, fontSize: "0.98rem" }}>
                  {h.text}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </section>
  );
};

export default Home;
